import type { Messages } from './de.js';
import { editor } from './en/editor.js';
import { model } from './en/model.js';
import { server } from './en/server.js';

export const en: Messages = {
  common: {
    cancel: 'Cancel',
    done: 'Done',
    ok: 'OK',
    close: 'Close',
    copy: 'Copy',
    delete: 'Delete',
    open: 'Open',
    rename: 'Rename “{name}”',
    renameAction: 'Rename',
    retry: 'Try again',
    loading: 'Loading …',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    unexpectedError: 'An unexpected error occurred.',
    language: 'Language',
  },
  app: {
    tagline: 'Hall and floor space planning',
    disclaimer: 'Concept/preliminary design – not for construction, permit or structural planning.',
    loadingEditor: 'Loading editor …',
    updateAvailable: 'A new version is available. Please save first.',
    reload: 'Reload',
    later: 'Later',
  },
  inspect: {
    toggle: 'View only',
    title:
      'View projects only: changes, preview images and working time are not saved, live sessions are not joined.',
    status: 'View only – nothing is saved',
    blocked: 'View only is on: nothing is saved.',
  },
  auth: {
    login: 'Sign in',
    logout: 'Sign out',
    createAccount: 'Create account',
    newAccount: 'Create a new account',
    haveAccount: 'I already have an account',
    registrationPending:
      'Thank you! Your registration has been received. You can sign in here as soon as the administrator has approved your account.',
    approvalNotice: 'New accounts are approved by the administrator.',
    registrationClosed: 'Access is granted by the administrator. Requests to info@ltt-3d.de.',
    accessCode: 'Access code',
    accessCodeContinue: 'Continue',
    accessCodeHint: 'You receive the access code from us. Requests to info@ltt-3d.de.',
  },
  password: {
    change: 'Change password',
    current: 'Current password',
    new: 'New password (at least {min} characters)',
    repeat: 'Repeat new password',
    mismatch: 'The two new passwords do not match',
    changed: 'Your password has been changed. You will need to sign in again on other devices.',
  },
  share: {
    button: 'Share',
    title: 'Share project',
    hint: 'Invitees need an InduSketch account. Editors plan along, viewers can look at and walk through the project.',
    owner: 'Owner',
    you: '(you)',
    roles: { editor: 'Can edit', viewer: 'View only' },
    roleAria: 'Role of {name}',
    emailLabel: 'Email address',
    invite: 'Invite',
    remove: 'Remove',
    confirmRemove: 'Remove access for {name}?',
    leave: 'Leave project',
    confirmLeave: 'Leave the project "{name}"? You will lose access.',
    noMembers: 'Not shared with anyone yet.',
    readOnly: 'View only – this project is read-only for you.',
    sharedBy: 'Shared by {name}',
    access: { owner: 'Owner', editor: 'Editor', viewer: 'View only' },
    status: { project: 'in the project', online: 'online in InduSketch', offline: 'offline' },
    call: 'Call',
    callAria: 'Call {name}',
    callAll: 'Call everyone',
    onlineElsewhere: {
      one: '{n} person is online but not in the project',
      other: '{n} people are online but not in the project',
    },
  },
  game: {
    title: 'The inspector is coming!',
    start: 'Game: The inspector is coming!',
    intro:
      'One person is the safety inspector, everybody else is staff and hides in the hall. Whoever the inspector sees up close is caught. Collect a helmet, high-visibility vest and safety shoes to be safe for 15 seconds. Nothing can be edited during the game.',
    inspector: 'Inspector',
    random: 'Random',
    duration: 'Duration',
    minutes: { one: '{n} minute', other: '{n} minutes' },
    go: 'Let’s go',
    noHall: 'The game needs a hall with enough free floor space in the project.',
    needPlayers: 'The game needs at least one more person in the project.',
    roleInspector: 'You are the inspector',
    roleStaff: 'You are staff',
    spectator: 'You are watching',
    phaseHiding: 'Hiding',
    phaseHunting: 'Inspection',
    gate: 'Signing in at the gate …',
    gateHint: 'The staff are hiding. You can start in a moment.',
    hideHint: 'Hide, the inspector is coming!',
    huntInspector: 'Find the staff: whoever you see up close is caught.',
    huntStaff: 'Collect helmet, vest and safety shoes: fully equipped you are safe for 15 seconds.',
    caught: 'Caught! Off to the safety briefing.',
    immune: 'Properly equipped: safe for {s} more seconds',
    free: 'free',
    caughtShort: 'caught',
    protectedShort: 'protected',
    ppe: { helmet: 'Helmet', vest: 'High-vis vest', shoes: 'Safety shoes' },
    winInspector: 'The inspector caught everybody!',
    winStaff: 'The staff got through the inspection!',
    again: 'Play again',
    end: 'End game',
    endConfirm: 'End the game for everybody?',
    you: '(you)',
  },
  mouse: {
    title: 'Mouse & navigation',
    wheel: 'Mouse wheel',
    zoomSpeed3d: 'Zoom speed 3D',
    zoomSpeed2d: 'Zoom speed 2D plan',
    zoomToCursor: 'Zoom to the mouse pointer (3D)',
    zoomToCursorHint:
      'The wheel zooms towards the point under the pointer instead of the view centre.',
    orbitSelection: 'Turn around the selected element (3D)',
    orbitSelectionHint:
      'A clicked element becomes the centre of rotation; double-click brings it into view.',
    orbitSkipLarge: 'Leave out sites, halls, office buildings and floors',
    orbitSkipLargeHint:
      'When they are selected, the point under the pointer stays the centre – a click beside an object no longer makes the zoom jump away.',
    invertWheel: 'Invert wheel direction',
    layout: 'Layout',
    bothPanels: 'Show both side panels at once',
    bothPanelsHint:
      'Off: on a tablet only one side panel is open at a time, so the plan and the 3D view get more room.',
    drag: 'Dragging with the mouse',
    rotateSpeed: 'Rotating the 3D view',
    panSpeed: 'Panning the 3D view',
    lookSpeed: 'Looking around while walking',
    percent: '{pct} %',
    hint: 'These settings apply to this device and browser.',
    reset: 'Reset to defaults',
  },
  live: {
    online: { one: '{n} person in the project', other: '{n} people in the project' },
    follow: 'Follow the view of {name}',
    jumpTo: 'Jump to {name}',
    stopFollow: 'Stop following',
    following: 'You are following {name}',
    disconnected: 'Live connection lost – reconnecting …',
    reconnected: 'Live connection restored.',
    resynced: 'A change no longer matched the current state and was discarded.',
    walking: 'walking through the hall',
    viewing2d: 'in the 2D plan',
    viewing3d: 'in 3D',
  },
  chat: {
    button: 'Chat',
    title: 'Project chat',
    close: 'Close chat',
    minimize: 'Minimise chat',
    expand: 'Open chat',
    unread: { one: '{n} unread message', other: '{n} unread messages' },
    placeholder: 'Write a message … (Enter sends, Shift+Enter new line)',
    send: 'Send',
    attach: 'Attach file',
    shareView: 'Share view',
    shareViewTitle: 'Send a picture of the current 3D view – the others can jump to this view',
    sharingView: 'Capturing the view …',
    dictate: 'Dictate',
    dictateTitle:
      'Speak a message (German, English or Polish) – click again to finish, Esc cancels',
    dictateStop: 'Finish dictation ({time})',
    transcribing: 'Recognising speech …',
    dictationEmpty: 'Nothing was understood – please speak again.',
    toPlan: 'Back to the plan',
    placeholderShort: 'Message …',
    download: 'Download',
    closeImage: 'Close picture',
    zoomHint: 'Double-tap to zoom',
    uploading: 'Uploading … {percent} %',
    jumpToView: 'Jump to this view',
    older: 'Load older messages',
    empty: 'No messages yet. Write the first one – it is translated automatically.',
    notShared: 'Share the project so others can read, reply and join calls.',
    translatedFrom: 'translated from {lang}',
    showOriginal: 'Show original texts',
    original: 'Original',
    showTranslation: 'Show translation',
    delete: 'Delete message',
    deleteConfirm: 'Really delete this message?',
    you: 'You',
    dropHint: 'Drop files here',
    call: 'Call',
    callRunning: 'Call in progress',
    callParticipants: 'Participants: {names}',
    callNoProtocol: 'No transcript – nothing was translated.',
    callShow: { one: 'Show {n} sentence', other: 'Show {n} sentences' },
    callHide: 'Hide transcript',
    languages: { de: 'German', en: 'English', pl: 'Polish' },
  },
  call: {
    title: 'Voice call with interpreter',
    button: 'Call',
    join: 'Join the call',
    start: 'Start a call',
    joining: 'Connecting …',
    connecting: 'connecting …',
    direct: 'direct',
    relayed: 'via relay',
    leave: 'Hang up',
    mute: 'Mute',
    unmute: 'Unmute',
    speaks: 'I speak',
    hears: 'I hear',
    original: 'Hear the original quietly',
    provider: 'Interpreter',
    providers: {
      gemini: 'Gemini – fast, voice follows the speaker',
      deepl: 'DeepL – technical terms, steady voice',
    },
    noProvider: 'No interpreter set up – you hear each other in the original.',
    members: { one: '{n} person in the call', other: '{n} people in the call' },
    consent:
      'The call is translated and saved as a transcript in the project chat. All project members can read it.',
    consentOk: 'Agree and join',
    cancel: 'Cancel',
    status: {
      off: 'No translation needed',
      connecting: 'Interpreter connecting …',
      on: 'Interpreter active',
      error: 'Interpreter unavailable',
    },
    micDenied: 'No access to the microphone – please allow it in the browser.',
    needsLive: 'Calls work only in shared projects with a live connection.',
    lostLive: 'The connection was interrupted – please join again.',
    interpreterLost: 'The interpreter lost the connection.',
    providerChanged: '{name} switched everybody to {provider}.',
    providerForAll: 'Applies to everybody in the call.',
    fallbackOriginal: 'You now hear each other in the original.',
    peerFailed: 'No connection possible – the network blocks it. Trying again …',
    headphones: 'Tip: it sounds best with headphones.',
    titleShort: 'Voice call',
    settings: 'Languages and interpreter',
    incoming: '{name} is calling',
    running: 'Call in progress with {names}',
    accept: 'Accept',
    decline: 'Decline',
    later: 'Later',
    acceptNote: 'By joining you agree that the call is translated and recorded in the chat.',
    notificationBody: 'Accept or decline in the project',
    ringAgain: 'Ring again',
    ringing: 'ringing …',
    ringingElsewhere: 'ringing (elsewhere in InduSketch) …',
    offline: 'not online',
    incomingProject: '{name} is calling – project “{project}”',
    acceptSwitch: 'Accepting switches to this project; the open one is saved first.',
    callEnded: 'The call has already ended.',
    declined: 'declined',
    notInCall: 'not in the call',
    nobodyOnline:
      'Nobody else is in the project right now. Whoever opens it sees a hint about the call.',
    you: 'You',
    speaking: 'speaking',
  },
  projects: {
    title: 'Projects',
    admin: 'Administration',
    newPlaceholder: 'Name of the new project, e.g. “Hall North”',
    projectName: 'Project name',
    create: 'New project',
    import: 'Import project',
    importing: 'Importing …',
    importHint: 'Open an exported InduSketch file as a new project',
    search: 'Search …',
    searchLabel: 'Search projects',
    sort: 'Sort order',
    sortUpdated: 'Last modified',
    sortName: 'Name',
    loading: 'Loading projects …',
    empty: 'No projects yet.',
    noMatch: 'No matches.',
    variantsOf: 'Variants of {name}',
    newName: 'New project name',
    changed: 'Modified {date}',
    variant: 'Variant',
    confirmDelete: 'Really delete project “{name}”?',
    actions: 'Actions for “{name}”',
    createVariant: 'Create variant',
    variants: { one: '{n} variant', other: '{n} variants' },
    emptyTitle: 'No projects yet',
    emptyHint:
      'Enter a name above and choose “New project” – or import an exported InduSketch file.',
    userMenu: 'User menu',
    activeNow: 'Being edited now: {names}',
  },
  assets: {
    title: 'My 3D models',
    empty: 'No models of your own yet. Drag a GLB file here or upload one.',
    upload: 'Upload model',
    uploading: 'Uploading …',
    dropHint: 'Drop GLB file here',
    dimensions: 'Width × depth × height',
    actions: 'Actions for “{name}”',
    triangles: { one: '{n} triangle', other: '{n} triangles' },
    newName: 'New name',
    confirmDelete: 'Delete model “{name}”?',
    unused: 'It is not used in any project.',
    usedIn: 'It is still used in:',
    placeholderAfter: 'A placeholder box of the same size will appear there instead.',
  },
  topbar: {
    toProjects: 'Back to projects',
    toggleTools: 'Show/hide tools',
    toggleProperties: 'Show/hide properties',
    more: 'More',
    unsaved: 'Unsaved changes',
    unsavedShort: 'unsaved',
    view: 'View',
    plan2d: '2D plan',
    view3d: '3D',
    top: 'Top',
    front: 'Front',
    side: 'Side',
    fit: 'Show all',
    findFar: 'Farthest element',
    findFarTitle:
      'Jumps to the element lying farthest outside (e.g. dropped far away by mistake); click again for the next one',
    farFound: '{name}: {distance} {reference} ({k} of {n})',
    farReference: {
      sites: 'outside the parcel',
      buildings: 'away from the buildings',
      center: 'from the middle of the project',
    },
    farNone: 'All elements lie on the parcel or at the buildings.',
    undo: 'Undo',
    undoTitle: 'Undo (Ctrl+Z)',
    redo: 'Redo',
    redoTitle: 'Redo (Ctrl+Y)',
    versions: 'Versions',
    versionsTitle: 'Version history',
    pdf: 'PDF',
    pdfTitle: 'Create PDF design report',
    export: 'Export',
    exporting: 'Export …',
    exportTitle: 'Export the project as a file (incl. your own 3D models), e.g. to pass it on',
    save: 'Save',
    saving: 'Saving …',
    saveError: 'Not saved – retrying shortly',
    conflict: 'Conflict',
    pending: 'Changes …',
    savedAt: 'Saved {time}',
  },
  versions: {
    title: 'Version history',
    labelPlaceholder: 'Label, e.g. “Review with hall builder”',
    labelAria: 'Version label',
    create: 'Save version',
    loading: 'Loading versions …',
    empty: 'No versions yet. Automatic backups are created when saving.',
    auto: 'Automatic backup',
    autoShort: 'Automatic',
    meta: '{date} · {user} · revision {revision}',
    restore: 'Restore',
    confirmRestore: 'Restore version “{label}”? The current state is saved as a version first.',
    saveFirst: 'Please save first or resolve the conflict.',
    delete: 'Delete version',
    confirmDelete: 'Delete version “{label}” for good?',
    prune: 'Delete automatic backups older than {days} days ({n})',
    confirmPrune:
      'Delete all automatic backups older than {days} days for good? Named versions are kept.',
    pruned: { one: '{n} backup deleted.', other: '{n} backups deleted.' },
  },
  conflict: {
    title: 'The project was changed in the meantime',
    text: 'Someone (or another tab) saved this project after you opened it. Your changes have not been saved yet and nothing has been overwritten.',
    saveCopy: 'Save my version as a copy',
    loadServer: 'Load the server state (discard my changes)',
    overwrite: 'Overwrite anyway',
    copyName: '{name} (copy)',
  },
  admin: {
    title: 'Administration',
    back: '← Projects',
    tabs: 'Sections',
    tabUsers: 'Users',
    tabProjects: 'Projects',
    tabAssets: 'Models',
    tabAudit: 'Audit log',
    tabPrices: 'Prices',
    pricesIntro:
      'Unit prices (net, €) and surcharges of the cost estimate. Every project uses the default catalog or the one it chose and can override single prices itself. Empty fields use the guide value.',
    pricesCatalogs: 'Price catalogs',
    pricesDefaultBadge: 'Default',
    pricesCopy: '+ Add copy',
    pricesCopyName: 'Copy of {name}',
    pricesName: 'Name',
    pricesCountry: 'Country',
    pricesIsDefault: 'Default for all projects without their own choice',
    pricesItem: 'Item',
    pricesGuide: 'Guide value',
    pricesCatalogPrice: 'Catalog price',
    pricesSave: 'Save catalog',
    pricesDelete: 'Delete catalog',
    pricesConfirmDelete:
      'Delete price catalog “{name}”? Projects that chose it then use the default catalog.',
    pricesSaved: 'Saved',
    pricesUnsaved: 'Unsaved changes',
    pricesCatalog: 'Catalog',
    pricesUnitPrices: 'Unit prices',
    pricesSurcharges: 'Surcharges',
    tabActivity: 'Activity',
    colLastActive: 'Last active',
    never: 'never',
    activitySummary: 'Users at a glance',
    activityTimeline: 'Timeline',
    colDevice: 'Device',
    colLocation: 'Location',
    colIp: 'IP address',
    colWorkGroup: 'Working time',
    col30Group: 'Last 30 days',
    colWork7: '7 days',
    colWork30: '30 days',
    colLogins: 'Sign-ins',
    colFailed: 'Failed attempts',
    online: 'Active now',
    activeIn: 'now in “{project}”',
    sortBy: 'Sort by',
    sortAsc: 'ascending',
    sortDesc: 'descending',
    colUser: 'User',
    colEvent: 'Event',
    activityKinds: {
      login: 'Sign-in',
      logout: 'Sign-out',
      'login-failed': 'Failed attempt',
      work: 'Work session',
    },
    workIn: '{duration} in “{project}”',
    workPlain: '{duration} in the editor',
    minutes: '{n} min',
    hoursMinutes: '{h} h {m} min',
    activityEmpty: 'No activity recorded yet.',
    activityNote:
      'Sign-ins, sign-outs, failed attempts and active working time in the editor are recorded. Entries are deleted automatically after {days} days. IP geolocation: DB-IP.com (CC BY 4.0).',
    filterActivity: 'Search by name, email, location or IP …',
    filterActivityAria: 'Filter activity',
    pending: 'Awaiting approval ({n})',
    registered: 'registered {date}',
    approve: 'Approve',
    reject: 'Reject',
    confirmReject: 'Reject the registration of {email}? The account will be deleted.',
    confirmReset: 'Generate a new password for {email}? The previous one will stop working.',
    newPassword: 'New password',
    accountCreated: 'Account created',
    oneTimePassword:
      'One-time password for {email}. It is only shown now – please pass it on securely. The user can replace it after signing in under “Change password”.',
    colName: 'Name',
    colEmail: 'Email',
    colRole: 'Role',
    colProjects: 'Projects',
    colSince: 'Since',
    colStatus: 'Status',
    roleAdmin: 'Administrator',
    roleUser: 'User',
    disabled: 'locked',
    active: 'active',
    ownAccount: 'own account',
    makeUser: 'Make user',
    makeAdmin: 'Make admin',
    unlock: 'Unlock',
    lock: 'Lock',
    resetPassword: 'Reset password',
    addUser: '+ Add user',
    createUser: 'Add user',
    role: 'Role',
    createAndPassword: 'Create and generate password',
    filterProjects: 'Search by project or owner …',
    filterProjectsAria: 'Filter projects',
    colProject: 'Project',
    colOwner: 'Owner',
    colRevision: 'Revision',
    colSize: 'Size',
    colChanged: 'Modified',
    uploading: 'Uploading …',
    uploadSystem: 'Add model to the system library (GLB)',
    colModel: 'Model',
    colLibrary: 'Library',
    colDimensions: 'Dimensions (W × D × H)',
    colUploaded: 'Uploaded',
    system: 'System',
    auditEmpty: 'No entries yet.',
    statUsers: 'Active users',
    statPending: 'Pending approvals',
    statProjects: 'Projects',
    statModels: 'Models',
    statStorage: 'Storage (models and projects)',
    filterUsers: 'Search by name or email …',
    filterUsersAria: 'Filter users',
    userActions: 'Actions for {email}',
    systemLibrary: 'System library',
    userModels: 'Users’ models',
    noUserModels: 'Users have not uploaded any models of their own yet.',
    noSystemModels: 'No models in the system library yet.',
    uploadedBy: 'by {name} · {date}',
    showMore: 'Show {n} more',
    colTime: 'Time',
    colAdmin: 'Administrator',
    colAction: 'Action',
    colTarget: 'Target',
    colDetails: 'Details',
    colActor: 'Done by',
    filterAudit: 'Search by person, action or target …',
    filterAuditAria: 'Filter audit log',
    allActions: 'All actions',
    actionFilter: 'Filter by action',
    loadOlder: 'Load older entries',
    targetTypes: {
      project: 'Project',
      user: 'User',
      asset: 'Model',
      'price-catalog': 'Price catalog',
    },
    detailRole: 'Role: {role}',
    detailWith: 'with {name}',
    detailRevision: 'from revision {n}',
    detailDefault: 'as default',
    detailLocked: 'locked',
    detailUnlocked: 'unlocked',
    detailApproved: 'approved',
    actions: {
      'project.update': 'Project changed',
      'project.live': 'Edited live',
      'project.meta': 'Project renamed',
      'project.delete': 'Project deleted',
      'project.restore': 'Version restored',
      'project.share': 'Project shared',
      'project.unshare': 'Sharing removed',
      'asset.delete': 'Model deleted',
      'asset.system-upload': 'System model uploaded',
      'user.update': 'User changed',
      'user.create': 'User created',
      'user.reject': 'Registration rejected',
      'user.password-reset': 'Password reset',
      'price-catalog.create': 'Price catalog created',
      'price-catalog.update': 'Price catalog changed',
      'price-catalog.delete': 'Price catalog deleted',
    },
  },
  network: {
    offline: 'No connection to the server',
    serverError: 'Server error ({status})',
    invalidResponse: 'Unexpected server response',
  },
  editor,
  model,
  server,
};
